'use client';

import { lazy, Suspense, useCallback, useState, type MouseEvent } from 'react';
import { SheetTrigger } from '@/components/ui/sheet';
import { useDeferredUnmount } from '@/hooks/use-deferred-unmount';
import { Button, Drawer, type AttributeShadeOption } from '@/shared-ui';
import { useTranslation } from 'react-i18next';

export type StockShadeOption = AttributeShadeOption & {
    availability: 'in-stock' | 'out-of-stock';
};

export interface QuickBuyProps {
    currency?: string;
    initialVariantSelections?: Record<string, string>;
    label?: string;
    productId: string;
    selectedColorValue?: string | null;
}

const QuickBuyDrawerContent = lazy(() =>
    import('./quick-buy-drawer-content').then((module) => ({ default: module.QuickBuyDrawerContent }))
);

export const QuickBuy = ({
    currency,
    initialVariantSelections,
    label,
    productId,
    selectedColorValue,
}: QuickBuyProps) => {
    const { t } = useTranslation('product');
    const [open, setOpen] = useState(false);
    const mounted = useDeferredUnmount(open);
    const buttonLabel = label ?? t('quickBuy', { defaultValue: 'Quick Buy' });

    const handleOpenDrawer = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setOpen(true);
    }, []);
    const handleAddToBag = useCallback(() => {
        setOpen(false);
    }, []);

    return (
        <Drawer.Root open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button type="button" onClick={handleOpenDrawer} className="w-full">
                    {buttonLabel}
                </Button>
            </SheetTrigger>
            {mounted && (
                <Suspense fallback={null}>
                    <QuickBuyDrawerContent
                        currency={currency}
                        initialVariantSelections={initialVariantSelections}
                        onAddToBag={handleAddToBag}
                        open={open}
                        productId={productId}
                        selectedColorValue={selectedColorValue}
                    />
                </Suspense>
            )}
        </Drawer.Root>
    );
};
